import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const STATUS_STYLE = {
  present: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  absent:  "text-red-400 bg-red-500/10 border-red-500/20",
  leave:   "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  wfh:     "text-blue-400 bg-blue-500/10 border-blue-500/20",
  late:    "text-orange-400 bg-orange-500/10 border-orange-500/20",
};

const HEAT_COLOR = {
  present: "bg-[#00ff90]/60", absent: "bg-red-500/50",
  leave: "bg-yellow-500/50", wfh: "bg-blue-500/50",
  late: "bg-orange-500/50",
};

const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

export default function MemberDrawer({ member, onClose, onRegularise }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!member?.id) return;
    const token = localStorage.getItem("token");
    setLoading(true);
    fetch(`${API_BASE}/api/manager/employee/${member.id}/attendance`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setDetail(d))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [member?.id]);

  if (!member) return null;

  const punches   = detail?.punches       || [];
  const heatmap   = detail?.heatmap       || Array.from({ length: 30 }, () => "other");
  const trend     = detail?.monthlyTrend  || [];  // [{month, rate}]
  const lateTrend = detail?.lateTrend     || [];  // [{week, lateMinutes}]
  const breakdown = detail?.breakdown     || null; // {present, absent, late, wfh, leave}

  // Build donut data from breakdown
  const donutData = breakdown ? [
    { name: "Present", value: breakdown.present || 0, color: "#00ff90" },
    { name: "Late",    value: breakdown.late    || 0, color: "#fb923c" },
    { name: "Absent",  value: breakdown.absent  || 0, color: "#f87171" },
    { name: "WFH",     value: breakdown.wfh     || 0, color: "#60a5fa" },
    { name: "Leave",   value: breakdown.leave   || 0, color: "#fbbf24" },
  ].filter(d => d.value > 0) : [];

  const attendanceRate = breakdown
    ? Math.round(((breakdown.present + (breakdown.wfh || 0)) / Math.max(1, Object.values(breakdown).reduce((a,b) => a+b, 0))) * 100)
    : null;

  return (
    <div className="fixed inset-0 z-[10060]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-[#0d0d0d] border-l border-[#00ff90]/20 shadow-[-20px_0_60px_#00ff9011] text-white flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div>
            <p className="font-semibold text-white">{member.name}</p>
            <p className="text-xs text-[#00ff90]/50 mt-0.5">{member.role}</p>
          </div>
          <div className="flex items-center gap-3">
            {attendanceRate != null && (
              <div className="text-right">
                <p className="text-lg font-bold text-[#00ff90]">{attendanceRate}%</p>
                <p className="text-xs text-white/30">Attendance rate</p>
              </div>
            )}
            <button onClick={onClose}
              className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {loading ? (
            <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-24 rounded-xl bg-white/5 animate-pulse" />)}</div>
          ) : (
            <>
              {/* Today's status */}
              {member.status && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/30 uppercase tracking-wider">Today</span>
                  <span className={`text-xs px-2.5 py-1 rounded-full border capitalize ${STATUS_STYLE[member.status?.toLowerCase()] || "text-white/30 bg-white/5 border-white/10"}`}>
                    {member.status}
                  </span>
                </div>
              )}

              {/* Status breakdown donut */}
              {donutData.length > 0 && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">30-Day Breakdown</p>
                  <div className="flex items-center gap-4">
                    <ResponsiveContainer width={100} height={100}>
                      <PieChart>
                        <Pie data={donutData} cx="50%" cy="50%" innerRadius={28} outerRadius={44} paddingAngle={2} dataKey="value">
                          {donutData.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.85} />)}
                        </Pie>
                        <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v} days`, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-1.5 flex-1">
                      {donutData.map(d => (
                        <div key={d.name} className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                            <span className="text-xs text-white/50">{d.name}</span>
                          </div>
                          <span className="text-xs font-medium" style={{ color: d.color }}>{d.value}d</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Attendance rate trend */}
              {trend.length > 0 && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Attendance Rate Trend</p>
                  <ResponsiveContainer width="100%" height={100}>
                    <LineChart data={trend} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                      <XAxis dataKey="month" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} unit="%" />
                      <Tooltip {...tooltipStyle} formatter={v => [`${v}%`, "Rate"]} />
                      <Line type="monotone" dataKey="rate" stroke="#00ff90" strokeWidth={2} dot={{ fill: "#00ff90", r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Late minutes trend */}
              {lateTrend.length > 0 && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Late Arrivals (mins) by Week</p>
                  <ResponsiveContainer width="100%" height={80}>
                    <BarChart data={lateTrend} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                      <XAxis dataKey="week" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                      <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                      <Tooltip {...tooltipStyle} formatter={v => [`${v} min`, "Late"]} />
                      <Bar dataKey="lateMinutes" fill="#fb923c" fillOpacity={0.7} radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Today's punches */}
              <div className="rounded-xl border border-[#00ff90]/15 bg-[#00ff90]/5 p-4">
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Today's Punches</p>
                {punches.length > 0 ? (
                  <ul className="space-y-1.5">
                    {punches.map((p, i) => (
                      <li key={i} className="flex items-center justify-between text-sm">
                        <span className="text-white/60">{p.type}</span>
                        <span className="font-mono text-white/80 text-xs">{p.time}</span>
                        <span className="text-white/30 text-xs">{p.source}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-white/20 text-sm italic">No punches recorded.</p>
                )}
              </div>

              {/* 30-day heatmap */}
              <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Last 30 Days</p>
                <div className="grid grid-cols-10 gap-1">
                  {heatmap.map((status, i) => (
                    <div key={i} title={status} className={`h-4 rounded-sm ${HEAT_COLOR[status] || "bg-white/10"}`} />
                  ))}
                </div>
                <div className="flex gap-3 mt-2 flex-wrap">
                  {[["present","#00ff90"],["absent","#f87171"],["leave","#fbbf24"],["wfh","#60a5fa"],["late","#fb923c"]].map(([s, c]) => (
                    <span key={s} className="flex items-center gap-1 text-xs text-white/25">
                      <span className="w-2 h-2 rounded-sm" style={{ background: c, opacity: 0.6 }} />{s}
                    </span>
                  ))}
                </div>
              </div>

              {/* Quick actions */}
              <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Quick Actions</p>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => onRegularise?.(member)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                    Approve Regularisation
                  </button>
                  <button className="text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 text-white/50 hover:bg-white/10 transition-colors">
                    Add Note
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}